import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc, getDoc, getDocs, setDoc, addDoc, updateDoc,
  collection, query, where, orderBy, onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import type { AppDispatch, RootState } from "./store";

/* ============================================================
   TYPES
   ============================================================ */
export type WebhookEndpoint = {
  id:                string;
  merchantId:        string;
  url:               string;
  enabled_events:    string[];
  description:       string | null;
  status:            "enabled" | "disabled";
  mode:              "live" | "test";
  has_secret:        boolean;
  secret_rotated_at: number | null;
  created:           number;
  createdAt:         string | null;
  updatedAt:         string | null;
  metadata?:         Record<string, string>;
};

type WebhookState = {
  endpoints:         WebhookEndpoint[];
  _unsub:            (() => void) | null;
  loading:           boolean;
  upserting:         boolean;
  error:             string | null;
};

/* ============================================================
   HELPERS
   ============================================================ */
const toISO = (ts: any): string | null =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseEndpoint = (id: string, raw: Record<string, any>): WebhookEndpoint => ({
  id,
  merchantId:        raw.merchantId        ?? "",
  url:               raw.url               ?? "",
  enabled_events:    raw.enabled_events    ?? [],
  description:       raw.description       ?? null,
  status:            raw.status            ?? "enabled",
  mode:              raw.mode              ?? "test",
  has_secret:        raw.has_secret        ?? false,
  secret_rotated_at: raw.secret_rotated_at ?? null,
  created:           raw.created           ?? 0,
  createdAt:         toISO(raw.createdAt),
  updatedAt:         toISO(raw.updatedAt),
  metadata:          raw.metadata          ?? {},
});

/* ============================================================
   THUNK 1 — upsertWebhook
   Pass id to update existing, omit to create new.
   ============================================================ */
export const upsertWebhook = createAsyncThunk<
  WebhookEndpoint,
  {
    merchantId:     string;
    url:            string;
    enabled_events: string[];
    description?:   string;
    mode:           "live" | "test";
    metadata?:      Record<string, string>;
    id?:            string;   // pass to update existing
    status?:        "enabled" | "disabled";
  }
>(
  "webhooks/upsert",
  async (data, { rejectWithValue }) => {
    try {
      if (data.id) {
        // ── UPDATE ──
        const ref = doc(db, "webhooks", data.id);
        const { id: _, merchantId: __, ...rest } = data;
        await updateDoc(ref, { ...rest, updatedAt: serverTimestamp() });
        const snap = await getDoc(ref);
        return parseEndpoint(snap.id, snap.data()!);
      } else {
        // ── CREATE ──
        const { id: _, ...rest } = data;
        const newRef = await addDoc(collection(db, "webhooks"), {
          ...rest,
          status:            "enabled",
          has_secret:        false,
          secret_rotated_at: null,
          created:           Math.floor(Date.now() / 1000),
          createdAt:         serverTimestamp(),
          updatedAt:         serverTimestamp(),
        });
        // write id into doc
        await updateDoc(doc(db, "webhooks", newRef.id), { id: newRef.id });
        const snap = await getDoc(doc(db, "webhooks", newRef.id));
        return parseEndpoint(snap.id, snap.data()!);
      }
    } catch (e: any) {
      return rejectWithValue(e.message ?? "Failed to upsert webhook");
    }
  }
);

/* ============================================================
   THUNK 2 — fetchWebhooks
   One-time fetch for a merchantId + mode.
   ============================================================ */
export const fetchWebhooks = createAsyncThunk<
  WebhookEndpoint[],
  { merchantId: string; mode: "live" | "test" }
>(
  "webhooks/fetch",
  async ({ merchantId, mode }, { rejectWithValue }) => {
    try {
      const snap = await getDocs(
        query(
          collection(db, "webhooks"),
          where("merchantId", "==", merchantId),
          where("mode", "==", mode),
          orderBy("created", "desc")
        )
      );
      return snap.docs.map(d => parseEndpoint(d.id, d.data()));
    } catch (e: any) {
      return rejectWithValue(e.message ?? "Failed to fetch webhooks");
    }
  }
);

/* ============================================================
   LISTENER — listenToWebhooks
   Real-time onSnapshot for a merchantId + mode.
   ============================================================ */
export const listenToWebhooks =
  (merchantId: string, mode: "live" | "test") =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    // clean up existing listener
    getState().webhooks._unsub?.();

    const unsub = onSnapshot(
      query(
        collection(db, "webhooks"),
        where("merchantId", "==", merchantId),
        where("mode", "==", mode),
        orderBy("created", "desc")
      ),
      snap => dispatch(setEndpoints(snap.docs.map(d => parseEndpoint(d.id, d.data())))),
      err  => dispatch(setError(err.message))
    );

    dispatch(setUnsub(unsub));
    return unsub;
  };

export const cleanupWebhookListener =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    getState().webhooks._unsub?.();
    dispatch(setUnsub(null));
  };

/* ============================================================
   SLICE
   ============================================================ */
const webhookSlice = createSlice({
  name: "webhooks",
  initialState: {
    endpoints: [],
    _unsub:    null,
    loading:   false,
    upserting: false,
    error:     null,
  } as WebhookState,
  reducers: {
    setEndpoints: (s, a: PayloadAction<WebhookEndpoint[]>) => { s.endpoints = a.payload; },
    setUnsub:     (s, a: PayloadAction<(() => void) | null>) => { s._unsub = a.payload; },
    setError:     (s, a: PayloadAction<string | null>)       => { s.error = a.payload; },
    clearError:   s => { s.error = null; },
  },
  extraReducers: builder => {
    // fetchWebhooks
    builder
      .addCase(fetchWebhooks.pending,   s => { s.loading = true; s.error = null; })
      .addCase(fetchWebhooks.fulfilled, (s, a) => { s.loading = false; s.endpoints = a.payload; })
      .addCase(fetchWebhooks.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; });

    // upsertWebhook
    builder
      .addCase(upsertWebhook.pending,   s => { s.upserting = true; s.error = null; })
      .addCase(upsertWebhook.fulfilled, (s, a) => {
        s.upserting = false;
        const idx = s.endpoints.findIndex(e => e.id === a.payload.id);
        idx >= 0 ? (s.endpoints[idx] = a.payload) : s.endpoints.unshift(a.payload);
      })
      .addCase(upsertWebhook.rejected,  (s, a) => { s.upserting = false; s.error = a.payload as string; });
  },
});

export const { setEndpoints, setUnsub, setError, clearError } = webhookSlice.actions;
export default webhookSlice.reducer;

/* ============================================================
   SELECTORS
   ============================================================ */
export const selectWebhooks          = (s: RootState) => s.webhooks.endpoints;
export const selectWebhookLoading    = (s: RootState) => s.webhooks.loading;
export const selectWebhookUpserting  = (s: RootState) => s.webhooks.upserting;
export const selectWebhookError      = (s: RootState) => s.webhooks.error;